import { useState, useCallback } from 'react';
import { cartService, handleApiError, CartResponse, CartItem, AddToCartRequest } from '../services';
import { trackEvent } from '../services/analytics';

export interface UseApiCartReturn {
  cart: CartResponse | null;
  cartItems: CartItem[];
  totalItems: number;
  totalAmount: number;
  isLoading: boolean;
  error: string | null;
  loadCart: () => Promise<void>;
  addToCart: (item: AddToCartRequest) => Promise<void>;
  updateCartItem: (productId: string, quantity: number) => Promise<void>;
  removeFromCart: (productId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  clearError: () => void;
}

export const useApiCartHook = (): UseApiCartReturn => {
  const [cart, setCart] = useState<CartResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const cartData = await cartService.getCartItems();
      setCart(cartData);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      // Set empty cart on error
      setCart({
        user_id: '',
        items: [],
        total_items: 0,
        total_amount: 0
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addToCart = useCallback(async (item: AddToCartRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await cartService.addToCart(item);
      // Reload cart to get updated data
      await loadCart();
      // Fire-and-forget analytics event -- helper is a no-op on
      // non-prod envs so this doesn't spam GA from dev/staging.
      trackEvent('add_to_cart', {
        product_id: item.product_id,
        quantity: item.quantity,
      });
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [loadCart]);

  const updateCartItem = useCallback(async (productId: string, quantity: number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await cartService.updateCartItem(productId, { quantity });
      // Reload cart to get updated data
      await loadCart();
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [loadCart]);

  const removeFromCart = useCallback(async (productId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await cartService.removeFromCart(productId);
      // Reload cart to get updated data
      await loadCart();
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [loadCart]);

  const clearCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await cartService.clearCart();
      // Reset cart state
      setCart(null);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    cart,
    cartItems: cart?.items || [],
    totalItems: cart?.total_items || 0,
    totalAmount: cart?.total_amount || 0,
    isLoading,
    error,
    loadCart,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    clearError,
  };
};